<template>
  <div>
    <div
      class="meridia-input-wrapper"
      :class="[
        sizeClass,
        stateClass,
        getPt < PassThroughMeridiaInput > ('wrapper', passThrough)?.class,
      ]"
    >
      <MeridiaIcon
        v-if="icon && icon_position === InputIconPosition.LEFT"
        class="icon"
        v-bind="getPt < PassThroughMeridiaInput > ('icon', passThrough)"
        :name="icon"
        type="material-icons-outlined"
      />
      <PrimeInput
        v-model="modelValue"
        unstyled
        :type="type"
        :pt="passThrough"
        :placeholder="placeholder"
        :disabled="state === InputState.DISABLED"
        class="meridia-input"
        :class="getPt < PassThroughMeridiaInput > ('input', passThrough)?.class"
        v-bind="$attrs"
      />
      <div class="w-4 h-4 flex justify-center items-center">
        <transition name="fade">
          <MeridiaIcon
            v-if="modelValue"
            class="icon-clear"
            :class="
              getPt <
              PassThroughMeridiaInput >
              ('clearIcon', passThrough)?.class
            "
            @click="clear"
            name="cancel"
            type="material-icons"
          />
        </transition>
      </div>
      <MeridiaIcon
        v-if="icon && icon_position === InputIconPosition.RIGHT"
        class="icon"
        v-bind="getPt < PassThroughMeridiaInput > ('icon', passThrough)"
        :name="icon"
        type="material-icons-outlined"
      />
    </div>
    <transition name="fade">
      <span
        v-if="helper_text"
        class="helper-text"
        :class="
          getPt < PassThroughMeridiaInput > ('helper_text', passThrough)?.class
        "
        >{{ helper_text }}</span
      >
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  InputSize,
  InputIconPosition,
  InputState,
  PassThroughMeridiaInput,
} from './types';
import { usePassThrough } from 'primevue/passthrough';
import PrimeInput from 'primevue/inputtext';
import { getPt } from '../utils';
import MeridiaIcon from '../icon/MeridiaIcon.vue';

const modelValue = defineModel<string | undefined>({ required: true });

const props = withDefaults(
  defineProps<{
    size?: `${InputSize}`;
    icon_position?: `${InputIconPosition}`;
    placeholder?: string;
    helper_text?: string;
    state?: `${InputState}`;
    icon?: string;
    type?: string;
    pt?: PassThroughMeridiaInput;
  }>(),
  {
    size: InputSize.SMALL,
    icon_position: InputIconPosition.RIGHT,
  }
);

const emit = defineEmits<{
  (event: 'update:model-value', value: boolean): void;
  /**
   * fired when user clears the input
   */
  (event: 'clear'): void;
}>();

const sizeClass = computed(() => ({
  'size-small': props.size === InputSize.SMALL,
  'size-large': props.size === InputSize.LARGE,
}));

const stateClass = computed(() => ({
  'state-error': props.state === InputState.ERROR,
  'state-success': props.state === InputState.SUCCESS,
  'state-disabled': props.state === InputState.DISABLED,
  'state-typed': modelValue.value,
}));

const passThrough = usePassThrough(props.pt, {} as PassThroughMeridiaInput, {
  mergeSections: false,
  mergeProps: true,
}) as PassThroughMeridiaInput;

function clear() {
  if (props.state === InputState.DISABLED) return;
  emit('clear');
  modelValue.value = '';
}
</script>

<style lang="scss" scoped>
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 150ms ease-out;
}
</style>

<style src="./style.scss" scoped lang="scss"></style>
